<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount } from 'vue'
import { useI18n } from 'vue-i18n';  
import { Lang } from "@/config";      
import { useSystemConfigStoreHook } from '@/store/modules/systemConfig';
// import mapSvg from '@/assets/svg/home/page6/page6-map.svg';

const { t } = useI18n();
const sysStore = useSystemConfigStoreHook();
const mapSvgUrl = 'https://iiii-clone.oss-cn-shenzhen.aliyuncs.com/web/nbweb/assets/svg/home/page6/page6-map.svg';
const mapSvgContent = ref('');

const sectionRef = ref<HTMLElement | null>(null)
const rotationProgress = ref(0)

onMounted(async () => {
  try {
    const response = await fetch(mapSvgUrl);
    if (response.ok) {
      mapSvgContent.value = await response.text();
    } else {
    
    }
  } catch (error) {
      console.error('Failed to load SVG:', error);
  }

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.target === sectionRef.value) {
          // 计算旋转进度
          const rect = entry.boundingClientRect
          const windowHeight = window.innerHeight
          const sectionHeight = rect.height
          
          // 当元素进入视口 20% 时开始旋转
          const startPoint = windowHeight * 0.8
          // 当元素底部进入视口时完成旋转
          const endPoint = windowHeight - sectionHeight
          
          // 计算当前进度，使用更平滑的计算方式
          const progress = Math.min(
            Math.max(
              (startPoint - rect.top) / (startPoint - endPoint),
              0
            ),
            1
          )
          
          // 使用 requestAnimationFrame 来更新状态，确保动画流畅
          requestAnimationFrame(() => {
            rotationProgress.value = progress
          })
        }
      })
    },
    {
      // 增加 threshold 的密度，使计算更精确
      threshold: [0, 0.05, 0.1, 0.15, 0.2, 0.25, 0.3, 0.35, 0.4, 0.45, 0.5, 0.55, 0.6, 0.65, 0.7, 0.75, 0.8, 0.85, 0.9, 0.95, 1],
      rootMargin: '0px'
    }
  )

  if (sectionRef.value) {
    observer.observe(sectionRef.value)
  }

  onBeforeUnmount(() => observer.disconnect())
})
</script>

<template>
  <div class="perspective-container">
    <h2 class="text-[3rem] pt-[8rem] mb-[3rem] text-center font-[600] text-[#000]">
      {{ t('page6.map.headingPrefix') }}
      <span class="text-main-primary">{{ t('page6.map.headingHighlight') }}</span>
      {{ t('page6.map.headingSuffix') }}
    </h2>
    <div 
      ref="sectionRef" 
      class="flex  pt-[2rem] bg-main-primary/0 h-[55rem] rounded-[1rem] max-width mx-auto flex-col justify-center"
      :style="{
        transform: `perspective(1200px) rotateX(${6 * (1 - rotationProgress)}deg)`,
        willChange: 'transform',
        opacity: 0.876 + (0.124 * rotationProgress),
        transition: 'transform 0.2s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
      }"
    >
      <div
        v-html="mapSvgContent"
        :class="{ '!mt-[0rem]': sysStore.currentLang == Lang.LAN_EN }"
        class="ml-[6.3rem] mt-[-4.5rem] h-[52rem] object-cover transition-all duration-300 [&>svg]:h-full [&>svg]:w-full"
        :style="{
          opacity: rotationProgress,
          transform: `scale(${0.8 + (0.2 * rotationProgress)}) translateY(${-20 * (1 - rotationProgress)}px)`,
          transition: 'opacity 0.3s ease, transform 0.3s ease'
        }"
      />
      <div class="flex text-[1rem] text-[#3D3D3D] w-[38.5rem] mt-[-5.6rem] ml-[1.5rem] flex-col">
        {{ t('page6.map.desc') }}
      </div>
    </div>
  </div>
</template>

<style scoped>
/* 3D 效果相关样式 */
.perspective-container {
  perspective: 1200px;
  perspective-origin: center 50% 0;
  transform-style: preserve-3d;
  width: 100%;
  height: 100%;
  overflow: visible;
}

/* 优化性能 */
* {
  will-change: transform;
  backface-visibility: hidden;
  transform-style: preserve-3d;
}

/* 初始和离开状态 */
.animate-out {
  opacity: 0;
  transform: translateY(40px);
  transition: opacity 0.6s ease, transform 0.6s ease;
}

/* 进入视口的状态 */
.animate-in {
  opacity: 1;
  transform: translateY(0);
  transition: opacity 0.6s ease, transform 0.6s ease;
}
</style>